/**
 * Shared helper for wiring stock pages up to the dynamic
 * /api/og social-card endpoint. Each stock sub-page (analysts,
 * short-interest, options, etc.) sets its own og:title /
 * og:description, but they all share the same image — a 1200×630
 * card rendered from the company's ticker + logo. Keeping the URL
 * shape in one place means /api/og's query-param contract has a
 * single caller to update if it ever changes.
 *
 * Absolute URL pointing at the ted-beta1 deploy while we test the
 * new card design. Flip back to https://app.ortex.com/api/og before
 * shipping to prod — social-fetcher bots (Twitterbot, Slackbot,
 * Facebook's scraper) need a fully qualified URL.
 */
import { Stock } from "@/models/stock";
import type { StockShortKeyData } from "@/controllers/stock";
import { shortOgParams } from "@/lib/short-key-data";

export const OG_ENDPOINT = "https://app.ortex.com/api/og";

export interface StockOgImage {
    ogImageUrl: string;
    /**
     * Headline rendered ON the share card itself ("TICKER — Company
     * Name"). Also the right value for og:image:alt / twitter:image:alt
     * since alt text describes the image, not the page.
     */
    cardTitle: string;
}

/** `shortData` puts the short-interest headline numbers on the card (overview and short-interest tab). */
export function buildStockOgImage(stock: Stock, pageLabel?: string, shortData?: StockShortKeyData | null): StockOgImage {
    const cardTitle = stock.name ? `${stock.ticker}, ${stock.name}` : stock.ticker;
    const params = new URLSearchParams({
        title: cardTitle,
        type: "stock",
        ticker: stock.ticker,
    });
    // Eyebrow label reflects the current sub-page ("Overview",
    // "Short Interest", etc.) so each tab gets its own card.
    // Omitted callers fall back to TYPE_LABELS["stock"] = "Stock Snapshot".
    if (pageLabel) params.set("label", pageLabel);
    if (stock.security_id) params.set("sid", String(stock.security_id));
    for (const [key, value] of Object.entries(shortOgParams(shortData))) params.set(key, value);
    return { ogImageUrl: `${OG_ENDPOINT}?${params.toString()}`, cardTitle };
}
